import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import { useTheme } from '@/components/providers/theme-provider';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu,
  X,
  Home,
  Store,
  Workflow,
  Bot,
  BarChart3,
  Settings,
  Users,
  Shield,
  CreditCard,
  User,
  LogOut,
  Moon,
  Sun,
  ExternalLink,
  Search,
  Zap,
  ChevronLeft,
  ChevronRight,
  PlusCircle,
  Activity,
  DollarSign
} from 'lucide-react';
import { BrandLogo } from '@/components/ui/BrandLogo';
import { 
  UNIFIED_NAVIGATION_CONFIG,
  getMainNavigation,
  getAdminNavigation,
  hasNavigationAccess,
  UnifiedNavigationItem
} from '@/config/navigation';

interface AuthenticatedLayoutProps {
  children: React.ReactNode;
}

export function AuthenticatedLayout({ children }: AuthenticatedLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Load saved state from localStorage
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const { user, logout } = useSupabaseAuth();
  const { hasPermission, user: rbacUser } = useSupabaseAuth();

  // Save collapse state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/marketplace?search=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate('/marketplace');
    }
  };

  const userRole = rbacUser?.role || user?.role || 'user';

  // Use unified navigation configuration
  const mainNavigationItems = getMainNavigation();
  const adminNavigationItems = getAdminNavigation();

  const handleLogout = async () => {
    try {
      // First clear the auth state
      await logout();
      
      // Force a small delay to ensure state is cleared
      setTimeout(() => {
        // Navigate to auth page with logout state
        navigate('/auth', { 
          state: { 
            wasLoggedOut: true, 
            reason: 'user_logout'
          },
          replace: true // Replace current history entry to prevent going back to authenticated state
        });
      }, 100);
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, redirect to auth page
      navigate('/auth', { 
        state: { 
          wasLoggedOut: false, 
          reason: 'logout_error',
          error: error instanceof Error ? error.message : 'Unknown error'
        },
        replace: true
      });
    }
  };

  const isActiveRoute = (href: string) => {
    if (href.startsWith('http')) return false;
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  // Filter navigation items based on user permissions and roles
  const visibleMainItems = mainNavigationItems.filter(item => 
    hasNavigationAccess(item, userRole, user?.permissions || [], hasPermission)
  );
  
  const visibleAdminItems = adminNavigationItems.filter(item => 
    hasNavigationAccess(item, userRole, user?.permissions || [], hasPermission)
  );

  // Debug logging for super_admin users
  if (userRole === 'super_admin') {
    console.log('🔧 UNIFIED NAVIGATION DEBUG:', {
      userRole,
      totalMainItems: mainNavigationItems.length,
      visibleMainItems: visibleMainItems.length,
      totalAdminItems: adminNavigationItems.length,
      visibleAdminItems: visibleAdminItems.length,
      adminItemNames: visibleAdminItems.map(item => item.name),
      hasPricingManagement: visibleAdminItems.some(item => item.key === 'pricing-management')
    });
  }

  const sidebarContent = (
    <div id="authenticated-layout-sidebar-wrapper" className="flex flex-col h-full">
      {/* Logo */}
      <div id="authenticated-layout-sidebar-header" className="flex items-center justify-between p-4 border-b min-h-[73px]">
        <AnimatePresence mode="wait">
          {!sidebarCollapsed && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <BrandLogo size="md" showText={true} linkTo="/dashboard" />
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Desktop Collapse Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className={cn(
            'h-8 w-8 transition-all duration-300 hover:bg-accent hidden md:flex',
            sidebarCollapsed && 'mx-auto'
          )}
        >
          <motion.div
            animate={{ rotate: sidebarCollapsed ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <ChevronLeft className="h-4 w-4" />
          </motion.div>
        </Button>

        {/* Mobile Close Button */}
        <Button
          id="authenticated-layout-sidebar-close-btn"
          variant="ghost"
          size="sm"
          onClick={() => setSidebarOpen(false)}
          className="md:hidden"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Navigation */}
      <nav id="authenticated-layout-sidebar-nav" className="flex-1 overflow-y-auto p-2 space-y-1">
        {/* Main Navigation */}
        <div id="authenticated-layout-main-nav" className="space-y-1">
          {visibleMainItems.map((item) => {
            const Icon = item.icon;
            const isActive = isActiveRoute(item.href);
            const hasAccess = true; // Already filtered by hasNavigationAccess
            
            if (item.external) {
              return (
                <motion.div
                  key={item.name}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.1 }}
                >
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      "flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200 group relative",
                      "hover:bg-accent hover:text-accent-foreground text-muted-foreground",
                      sidebarCollapsed && 'justify-center px-2'
                    )}
                    title={sidebarCollapsed ? item.name : undefined}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    
                    <AnimatePresence mode="wait">
                      {!sidebarCollapsed && (
                        <motion.div
                          initial={{ opacity: 0, width: 0 }}
                          animate={{ opacity: 1, width: 'auto' }}
                          exit={{ opacity: 0, width: 0 }}
                          transition={{ duration: 0.2, delay: 0.1 }}
                          className="flex-1 min-w-0 overflow-hidden"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium truncate">{item.name}</span>
                            {item.badge && (
                              <Badge variant="secondary" className="ml-2 text-xs">
                                {item.badge}
                              </Badge>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    
                    {!sidebarCollapsed && <ExternalLink className="h-3 w-3" />}

                    {/* Tooltip for collapsed state */}
                    {sidebarCollapsed && (
                      <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-sm rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap">
                        {item.name}
                      </div>
                    )}
                  </a>
                </motion.div>
              );
            }

            return (
              <motion.div
                key={item.name}
                whileHover={{ scale: hasAccess ? 1.02 : 1.01 }}
                whileTap={{ scale: hasAccess ? 0.98 : 0.99 }}
                transition={{ duration: 0.1 }}
              >
                <Link
                  to={hasAccess ? item.href : '#'}
                  onClick={(e) => {
                    if (!hasAccess) {
                      e.preventDefault();
                      // Show tooltip or handle upgrade flow
                      return;
                    }
                    setSidebarOpen(false);
                  }}
                  className={cn(
                    "flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200 group relative",
                    hasAccess && isActive
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : hasAccess
                      ? "text-muted-foreground hover:text-foreground hover:bg-accent"
                      : "text-muted-foreground/50 cursor-not-allowed opacity-60",
                    sidebarCollapsed && 'justify-center px-2'
                  )}
                  title={sidebarCollapsed ? (hasAccess ? item.name : `${item.name} (Requires upgrade)`) : undefined}
                >
                  <Icon className={cn(
                    'h-4 w-4 flex-shrink-0 transition-colors',
                    hasAccess && isActive ? 'text-primary-foreground' : 'text-current'
                  )} />
                  
                  <AnimatePresence mode="wait">
                    {!sidebarCollapsed && (
                      <motion.div
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.2, delay: 0.1 }}
                        className="flex-1 min-w-0 overflow-hidden"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium truncate">{item.name}</span>
                          <div className="flex items-center gap-1">
                            {item.badge && (
                              <Badge variant={hasAccess && isActive ? "secondary" : "outline"} className="text-xs">
                                {item.badge}
                              </Badge>
                            )}
                            {!hasAccess && (
                              <Badge variant="outline" className="text-xs text-amber-600 border-amber-600">
                                Upgrade
                              </Badge>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Tooltip for collapsed state */}
                  {sidebarCollapsed && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-sm rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap">
                      {hasAccess ? item.name : `${item.name} (Requires ${item.permission === 'super_admin' ? 'Super Admin' : 'Seller+'} access)`}
                    </div>
                  )}
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Admin Section */}
        {visibleAdminItems.length > 0 && (
          <>
            <div id="authenticated-layout-admin-section" className="border-t pt-4 mt-4">
              <AnimatePresence mode="wait">
                {!sidebarCollapsed && (
                  <motion.h3 
                    id="authenticated-layout-admin-title" 
                    className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    Administration
                  </motion.h3>
                )}
              </AnimatePresence>
              <div id="authenticated-layout-admin-nav" className="space-y-1">
                {visibleAdminItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = isActiveRoute(item.href);
                  const hasAccess = true; // Already filtered by hasNavigationAccess

                  return (
                    <motion.div
                      key={item.name}
                      whileHover={{ scale: hasAccess ? 1.02 : 1.01 }}
                      whileTap={{ scale: hasAccess ? 0.98 : 0.99 }}
                      transition={{ duration: 0.1 }}
                    >
                      <Link
                        to={hasAccess ? item.href : '#'}
                        onClick={(e) => {
                          if (!hasAccess) {
                            e.preventDefault();
                            return;
                          }
                          setSidebarOpen(false);
                        }}
                        className={cn(
                          "flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200 group relative",
                          hasAccess && isActive
                            ? "bg-primary text-primary-foreground shadow-sm"
                            : hasAccess
                            ? "text-muted-foreground hover:text-foreground hover:bg-accent"
                            : "text-muted-foreground/50 cursor-not-allowed opacity-60",
                          sidebarCollapsed && 'justify-center px-2'
                        )}
                        title={sidebarCollapsed ? (hasAccess ? item.name : `${item.name} (Requires admin access)`) : undefined}
                      >
                        <Icon className={cn(
                          'h-4 w-4 flex-shrink-0 transition-colors',
                          hasAccess && isActive ? 'text-primary-foreground' : 'text-current'
                        )} />
                        
                        <AnimatePresence mode="wait">
                          {!sidebarCollapsed && (
                            <motion.div
                              initial={{ opacity: 0, width: 0 }}
                              animate={{ opacity: 1, width: 'auto' }}
                              exit={{ opacity: 0, width: 0 }}
                              transition={{ duration: 0.2, delay: 0.1 }}
                              className="flex-1 min-w-0 overflow-hidden"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium truncate">{item.name}</span>
                                {!hasAccess && (
                                  <Badge variant="outline" className="text-xs text-amber-600 border-amber-600">
                                    Admin
                                  </Badge>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {/* Tooltip for collapsed state */}
                        {sidebarCollapsed && (
                          <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-sm rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap">
                            {hasAccess ? item.name : `${item.name} (Requires admin approval)`}
                          </div>
                        )}
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </nav>

      {/* User Profile */}
      <div id="authenticated-layout-user-profile" className="border-t p-4">
        <AnimatePresence mode="wait">
          {!sidebarCollapsed ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button id="authenticated-layout-user-profile-btn" variant="ghost" className="w-full justify-start h-auto p-2">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user?.avatar_url || ''} alt={user?.display_name || 'User'} />
                        <AvatarFallback>
                          {user?.display_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 text-left">
                        <p className="text-sm font-medium truncate">
                          {user?.display_name || 'User'}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {userRole}
                        </p>
                      </div>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Profile Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      Account Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/subscription" className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Subscription
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
                    {theme === 'dark' ? (
                      <Sun className="h-4 w-4 mr-2" />
                    ) : (
                      <Moon className="h-4 w-4 mr-2" />
                    )}
                    Toggle Theme
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col items-center space-y-2"
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 group relative">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user?.avatar_url || ''} alt={user?.display_name || 'User'} />
                      <AvatarFallback>
                        {user?.display_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    {/* Tooltip */}
                    <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-sm rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap">
                      {user?.display_name || user?.email || 'User'}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Profile Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      Account Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/subscription" className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Subscription
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
                    {theme === 'dark' ? (
                      <Sun className="h-4 w-4 mr-2" />
                    ) : (
                      <Moon className="h-4 w-4 mr-2" />
                    )}
                    Toggle Theme
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );

  return (
    <div id="authenticated-layout-container" className="flex h-screen bg-background">
      {/* Desktop Sidebar */}
      <motion.div 
        id="authenticated-layout-sidebar-desktop" 
        className="hidden md:flex md:flex-col md:fixed md:inset-y-0"
        initial={false}
        animate={{ 
          width: sidebarCollapsed ? 64 : 256 
        }}
        transition={{ 
          duration: 0.3, 
          ease: [0.4, 0.0, 0.2, 1] 
        }}
      >
        <div id="authenticated-layout-sidebar-content" className="flex-1 flex flex-col min-h-0 border-r bg-card overflow-hidden">
          {sidebarContent}
        </div>
      </motion.div>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div id="authenticated-layout-mobile-sidebar-overlay" className="fixed inset-0 z-50 md:hidden">
          <div 
            id="authenticated-layout-mobile-sidebar-backdrop"
            className="fixed inset-0 bg-black/50 transition-opacity" 
            onClick={() => setSidebarOpen(false)} 
          />
          <div id="authenticated-layout-mobile-sidebar" className="fixed left-0 top-0 bottom-0 w-64 bg-card border-r shadow-lg transform transition-transform">
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Main Content */}
      <motion.div 
        id="authenticated-layout-main-content" 
        className="flex-1 flex flex-col overflow-hidden"
        initial={false}
        animate={{ 
          marginLeft: isDesktop ? (sidebarCollapsed ? 64 : 256) : 0
        }}
        transition={{ 
          duration: 0.3, 
          ease: [0.4, 0.0, 0.2, 1] 
        }}
      >
        {/* Top Header - Conditional Content for Workflow Pages */}
        <header id="authenticated-layout-header" className="bg-background/80 backdrop-blur-sm border-b px-4 py-3 flex items-center justify-between md:justify-end">
          {/* Mobile Menu Button */}
          <Button
            id="authenticated-layout-mobile-menu-btn"
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(true)}
            className="md:hidden"
          >
            <Menu className="h-5 w-5" />
          </Button>

          {/* Conditional Search Bar - Hide on workflow pages */}
          {!location.pathname.includes('/workflows') && (
            <div id="authenticated-layout-search" className="flex-1 max-w-md mx-4 hidden sm:block">
              <form onSubmit={handleSearch} className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-muted-foreground" />
                </div>
                <Input
                  id="authenticated-layout-search-input"
                  className="pl-10 w-full"
                  placeholder="Search agents..."
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </form>
            </div>
          )}

          {/* Mobile Search Button - Hide on workflow pages */}
          {!location.pathname.includes('/workflows') && (
            <Button 
              id="authenticated-layout-mobile-search"
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/marketplace')}
              className="sm:hidden"
            >
              <Search className="h-4 w-4" />
              <span className="sr-only">Search agents</span>
            </Button>
          )}

          {/* Right Side Actions - Simplified for workflow pages */}
          <div id="authenticated-layout-header-actions" className="flex items-center gap-2">
            {/* Role Badge - Hide on workflow pages */}
            {!location.pathname.includes('/workflows') && (
              <Badge id="authenticated-layout-role-badge" variant="outline" className="hidden sm:flex">
                {userRole}
              </Badge>
            )}
            
            {/* Theme Toggle - Hide on workflow pages for cleaner interface */}
            {!location.pathname.includes('/workflows') && (
              <Button
                id="authenticated-layout-theme-toggle"
                variant="ghost"
                size="sm"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className="hidden md:flex"
              >
                {theme === 'dark' ? (
                  <Sun className="h-4 w-4" />
                ) : (
                  <Moon className="h-4 w-4" />
                )}
                <span className="sr-only">Toggle theme</span>
              </Button>
            )}
          </div>
        </header>

        {/* Page Content - Dynamic padding for workflow pages */}
        <main 
          id="authenticated-layout-page-content" 
          className={cn(
            "flex-1 overflow-auto",
            location.pathname.includes('/workflows') ? 'p-0' : 'p-6'
          )}
        >
          {children}
        </main>
      </motion.div>
    </div>
  );
}
